import { useMemo } from 'react'
import { formatPercent, formatUSD } from '@/lib/format-utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { CeoTable } from './ceo-table'
import { Disclosure } from './disclosure'

const percent = (value) => formatPercent(value, 0, 1)
const money = (value) => formatUSD(value, 0, 0)

// 回本线 100%，±3 点为预测误差带（决策盘口径 CEO 2026-09-09）
const D90_RED_BELOW = 0.97
const D90_YELLOW_TO = 1.03

export const REASONS = {
  below_min_daily_cost: '日均花费 < $20',
  insufficient_multiplier_samples: '倍数或 D30 样本不足',
  missing_cohort_data: '缺失队列回传',
  observation: '观察格（付费不满 90 天或满 90 天付费花费 < $1,000）',
}

const d90Tone = (value) => {
  if (value == null) return ''
  if (value < D90_RED_BELOW) return 'red'
  if (value <= D90_YELLOW_TO) return 'yellow'
  return ''
}

const gapText = (gap) => {
  if (gap == null) return '-'
  const points = gap * 100
  return points > 0 ? `差 ${points.toFixed(1)}` : `超 ${(-points).toFixed(1)}`
}

const COLUMNS = [
  '日均花费（近 28 天）',
  '花费占比',
  'D7 实测',
  'D30 实测',
  '预测 D90',
  '预测 D180',
  '回本日',
  'D180 贡献毛利 / 天',
  '可达线 D180',
  '距回本线（点）',
]

function Cells({ row, share }) {
  const tone = d90Tone(row.d90)
  return (
    <>
      <TableCell className="text-right tabular-nums">{money(row.dailyCost)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(share)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.d7)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.d30)}</TableCell>
      <TableCell
        className={cn(
          'text-right font-medium tabular-nums',
          tone === 'red' && 'text-destructive',
          tone === 'yellow' && 'text-warning',
        )}
      >
        {percent(row.d90)}
      </TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.d180)}</TableCell>
      <TableCell className="text-right tabular-nums">
        {row.paybackDay == null ? '>180' : `D${row.paybackDay}`}
      </TableCell>
      <TableCell className="text-right tabular-nums">{money(row.profit180)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.attainableD180)}</TableCell>
      <TableCell className="text-right tabular-nums">{gapText(row.gapToPayback)}</TableCell>
    </>
  )
}

const rowTone = (row) => {
  const tone = d90Tone(row.d90)
  return cn(
    tone === 'red' && 'bg-destructive/10 hover:bg-destructive/20',
    tone === 'yellow' && 'bg-warning/10 hover:bg-warning/20',
  )
}

/**
 * 下钻表（业务群 / 国家对比）：每行一个下一层节点，表底合计行 = 当前范围；
 * onSelect(id) 把范围收窄到该行（国家行不再下钻，不传）
 */
export function GroupTable({ title, firstColumn, rows, total, totalLabel, onSelect }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <CeoTable>
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead>{firstColumn}</TableHead>
              {COLUMNS.map((column) => (
                <TableHead key={column} className="whitespace-nowrap text-right">{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(rowTone(row), onSelect && 'cursor-pointer')}
                onClick={onSelect && (() => onSelect(row.id))}
              >
                <TableCell className="whitespace-nowrap font-medium">{row.name}</TableCell>
                <Cells row={row} share={row.share} />
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className={rowTone(total)}>
              <TableCell className="whitespace-nowrap font-medium">合计（{totalLabel}）</TableCell>
              <Cells row={total} share={1} />
            </TableRow>
          </TableFooter>
        </CeoTable>
      </CardContent>
    </Card>
  )
}

// 完整版进术语抽屉「标色与噪声规则」，排除说明里只留一句（MON-89）
export const OTHER_NOTE = '同一 pid 下日均 < $20、缺失队列回传、倍数或 D30 样本不足的国家合成一个「其他国家」格子，再按数据盘同一套规则计算；观察格不合并。CEO 2026-10-26 定，仅 CEO 页口径，待写入 knowledge。'
const OTHER_NOTE_SHORT = '同一 pid 下不进合计的小国合并为一格，按同一规则计算；观察格不合并'

const memberText = (member) =>
  `${member.country} · ${money(member.dailyCost)} / 天 · ${REASONS[member.reason] ?? member.reason}`

/** 合成格子包含的国家：按日均花费降序；近 28 天无花费的折成一行 */
function Members({ members }) {
  const sorted = [...members].sort((a, b) => b.dailyCost - a.dailyCost)
  const active = sorted.filter((member) => member.dailyCost > 0)
  const idle = sorted.filter((member) => !(member.dailyCost > 0))
  return (
    <ul className="max-h-48 overflow-auto pl-4 text-xs text-muted-foreground">
      {active.map((member) => (
        <li key={member.id}>{memberText(member)}</li>
      ))}
      {idle.length > 0 && (
        <li>
          <Disclosure summary={`另有 ${idle.length} 国近 28 天无花费`} className="text-xs">
            <ul className="pl-4">
              {idle.map((member) => (
                <li key={member.id}>{memberText(member)}</li>
              ))}
            </ul>
          </Disclosure>
        </li>
      )}
    </ul>
  )
}

/** 排除列表的一行：合成格子可展开看国家，其余照旧 */
export function ExcludedItem({ item, detail }) {
  if (!item.members?.length) return <li>{detail}</li>
  return (
    <li>
      <Disclosure summary={detail} className="text-xs">
        <Members members={item.members} />
      </Disclosure>
    </li>
  )
}

/** 其他国家合成格子：进合计的给出 D90 / D180，未进合计的给出原因 */
function OtherCells({ others }) {
  if (!others?.length) return null
  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium">其他国家格子</p>
      <p className="text-xs text-muted-foreground">{OTHER_NOTE_SHORT}</p>
      <ul className="flex flex-col gap-1">
        {others.map((other) => (
          <li key={other.id}>
            <Disclosure
              summary={
                <span>
                  {other.name} · {money(other.dailyCost)} / 天 · {percent(other.share)} ·{' '}
                  {other.reason == null
                    ? `进合计，预测 D90 ${percent(other.d90)} / D180 ${percent(other.d180)}`
                    : `不进合计：${REASONS[other.reason] ?? other.reason}`}
                </span>
              }
            >
              <Members members={other.members} />
            </Disclosure>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** 排除表：首列可展开，其余三列数字右对齐 */
export function ExcludedTable({ firstColumn, children }) {
  return (
    <CeoTable>
      <TableHeader className="bg-muted">
        <TableRow>
          <TableHead>{firstColumn}</TableHead>
          <TableHead className="text-right">格子数</TableHead>
          <TableHead className="text-right">日均花费</TableHead>
          <TableHead className="text-right">花费占比</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </CeoTable>
  )
}

/** 排除说明里的一节：标题 + 说明 + 内容，放在页头「排除说明」Sheet 里（MON-68） */
function ExcludedSection({ title, description, children }) {
  return (
    <section className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  )
}

/** 当前范围排除说明：按原因汇总，合成格子一行可展开 */
export function OverallExcluded({ title, data, row }) {
  const groups = useMemo(() => {
    const byReason = new Map()
    data.excluded.forEach((item) => {
      const group = byReason.get(item.reason) ?? { reason: item.reason, count: 0, dailyCost: 0, share: 0, items: [] }
      group.count += 1
      group.dailyCost += item.dailyCost
      group.share += item.share
      group.items.push(item)
      byReason.set(item.reason, group)
    })
    return [...byReason.values()].sort((a, b) => b.dailyCost - a.dailyCost)
  }, [data.excluded])
  return (
    <ExcludedSection
      title={title}
      description={
        <>
          合计只含 {row.cellCount} 个格子（日均 {money(row.modeledDailyCost)}，占近 28 天日均花费{' '}
          {percent(row.dailyCost > 0 ? row.modeledDailyCost / row.dailyCost : null)}）。下列格子不进合计，但花费计入「近 28 天日均花费」。
        </>
      }
    >
      <ExcludedTable firstColumn="原因">
        {groups.map((group) => (
          <TableRow key={group.reason} className="align-top">
            <TableCell>
              <Disclosure summary={REASONS[group.reason] ?? group.reason}>
                <ul className="flex max-h-48 flex-col gap-1 overflow-auto text-xs text-muted-foreground">
                  {group.items.map((item) => (
                    <ExcludedItem key={item.id} item={item} detail={`${item.name} · ${money(item.dailyCost)} / 天`} />
                  ))}
                </ul>
              </Disclosure>
            </TableCell>
            <TableCell className="text-right tabular-nums">{group.count}</TableCell>
            <TableCell className="text-right tabular-nums">{money(group.dailyCost)}</TableCell>
            <TableCell className="text-right tabular-nums">{percent(group.share)}</TableCell>
          </TableRow>
        ))}
        {!groups.length && (
          <TableRow>
            <TableCell colSpan={4} className="text-muted-foreground">无</TableCell>
          </TableRow>
        )}
      </ExcludedTable>
      <OtherCells others={data.others} />
    </ExcludedSection>
  )
}

/** 按下一层列出不进合计的格子：业务群 / pid / 国家（MON-93 同一张表） */
export function BreakdownExcluded({ title, description, firstColumn, rows, excluded, keyOf = (row) => row.id }) {
  const sections = useMemo(
    () =>
      rows.map((row) => {
        const items = excluded[keyOf(row)] ?? []
        return {
          id: row.id,
          name: row.name,
          items,
          dailyCost: items.reduce((acc, item) => acc + item.dailyCost, 0),
          share: items.reduce((acc, item) => acc + item.share, 0),
        }
      }),
    [rows, excluded, keyOf],
  )
  return (
    <ExcludedSection title={title} description={description}>
      <ExcludedTable firstColumn={firstColumn}>
        {sections.map((section) => (
          <TableRow key={section.id} className="align-top">
            <TableCell>
              {section.items.length ? (
                <Disclosure summary={section.name}>
                  <ul className="flex max-h-48 flex-col gap-1 overflow-auto text-xs text-muted-foreground">
                    {section.items.map((item) => (
                      <ExcludedItem
                        key={item.id}
                        item={item}
                        detail={`${item.name} · ${REASONS[item.reason] ?? item.reason} · ${money(item.dailyCost)} / 天 · ${percent(item.share)}`}
                      />
                    ))}
                  </ul>
                </Disclosure>
              ) : (
                section.name
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">{section.items.length}</TableCell>
            <TableCell className="text-right tabular-nums">{money(section.dailyCost)}</TableCell>
            <TableCell className="text-right tabular-nums">{percent(section.share)}</TableCell>
          </TableRow>
        ))}
      </ExcludedTable>
    </ExcludedSection>
  )
}

/** 排除说明按下一层列出不进合计的格子，花费占比为占当前范围日均花费 */
export function RowsExcluded({ title, firstColumn, breakdown }) {
  return (
    <BreakdownExcluded
      title={title}
      description="下列格子不进该行的预测与贡献毛利，但花费计入「日均花费」。花费占比为占当前范围近 28 天日均花费的比例。"
      firstColumn={firstColumn}
      rows={breakdown.rows}
      excluded={breakdown.excluded}
    />
  )
}
